import chroma from 'chroma-js';
import { BrandType } from './schema';
import {
  getLastDraftOrder,
  postOrder,
  updateOrder,
} from 'api/accessexpressv3/api';
import { PostDraftRequest } from 'api/accessexpressv3/schemas/OrdersSchemas';
import { QuoteLocationSchema } from 'api/accessexpress/schema';

export const lightTheme = {
  '--black': '13 18 25',
  '--white': '248 250 252',

  '--neutral-100': '245 249 255',
  '--neutral-200': '234 241 251',
  '--neutral-300': '215 227 243',
  '--neutral-400': '186 202 225',
  '--neutral-500': '144 167 199',
  '--neutral-600': '105 133 167',
  '--neutral-700': '78 103 134',
  '--neutral-800': '57 75 99',
  '--neutral-900': '39 49 69',

  '--purple-100': '185 185 240',
  '--purple-200': '150 144 230',
  '--purple-300': '107 101 217',
  '--purple-400': '73 72 196',
  '--purple-500': '50 50 168',
  '--purple-600': '39 40 133',
  '--purple-700': '27 29 96',
  '--purple-800': '18 20 64',
  '--purple-900': '10 11 39',

  '--green-100': '215 255 215',
  '--green-200': '169 251 167',
  '--green-300': '112 231 112',
  '--green-400': '58 197 59',
  '--green-500': '47 155 43',
  '--green-600': '34 120 57',
  '--green-700': '27 98 48',
  '--green-800': '20 74 33',
  '--green-900': '13 51 19',

  '--teal-100': '217 248 255',
  '--teal-200': '169 239 255',
  '--teal-300': '112 223 255',
  '--teal-400': '58 197 230',
  '--teal-500': '43 142 163',
  '--teal-600': '31 113 132',
  '--teal-700': '23 93 106',
  '--teal-800': '18 71 79',
  '--teal-900': '13 47 52',

  '--blue-100': '211 225 255',
  '--blue-200': '169 198 255',
  '--blue-300': '109 158 255',
  '--blue-400': '63 125 238',
  '--blue-500': '33 86 196',
  '--blue-600': '16 54 127',
  '--blue-700': '27 27 43',
  '--blue-800': '21 21 31',
  '--blue-900': '15 15 20',

  '--purple2-100': '207 195 230',
  '--purple2-200': '132 119 184',
  '--purple2-300': '98 82 156',
  '--purple2-400': '76 61 133',
  '--purple2-500': '54 44 101',
  '--purple2-600': '36 31 79',
  '--purple2-700': '29 26 67',
  '--purple2-800': '18 16 52',
  '--purple2-900': '18 16 52',

  '--yellow-100': '252 231 169',
  '--yellow-200': '249 214 115',
  '--yellow-300': '246 193 42',
  '--yellow-400': '229 165 11',
  '--yellow-500': '195 139 22',
  '--yellow-600': '159 106 21',
  '--yellow-700': '124 76 18',
  '--yellow-800': '102 59 16',
  '--yellow-900': '85 48 15',

  '--orange-100': '255 226 197',
  '--orange-200': '255 208 164',
  '--orange-300': '242 144 71',
  '--orange-400': '221 106 37',
  '--orange-500': '201 89 28',
  '--orange-600': '179 66 23',
  '--orange-700': '147 49 15',
  '--orange-800': '116 40 13',
  '--orange-900': '95 33 11',

  '--red-100': '255 213 221',
  '--red-200': '255 161 181',
  '--red-300': '248 105 138',
  '--red-400': '231 61 100',
  '--red-500': '217 33 74',
  '--red-600': '180 21 56',
  '--red-700': '123 0 27',
  '--red-800': '95 0 20',
  '--red-900': '67 0 15',

  '--pink-100': '248 221 236',
  '--pink-200': '243 200 224',
  '--pink-300': '231 132 187',
  '--pink-400': '217 82 158',
  '--pink-500': '217 82 158',
  '--pink-600': '177 26 96',
  '--pink-700': '147 17 75',
  '--pink-800': '122 15 64',
  '--pink-900': '101 12 52',

  '--accent-primary': 'var(--blue-400)',
  '--accent-blue': 'var(--blue-400)',
  '--accent-purple': 'var(--purple2-300)',
  '--accent-teal': 'var(--teal-400)',
  '--accent-green': 'var(--green-300)',

  '--shadow-ae-product':
    '0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)',
  /* custom colors */
  '--background-ae-empty-cart': 'var(--background-base-surface-2)',
  '--background-ae-product': 'var(--background-base-surface-2)',
  '--background-ae-product-hover': 'var(--background-base-surface-1)',
  '--background-inventory-empty-box-hover': 'var(--background-base-surface-2)',
  '--background-inventory-recent-update-hover': 'var(--white)',
  '--background-table-subrow': 'var(--white)',
  '--background-ae-cart-item': 'var(--white)',
  '--background-ae-product-all-servic-tile-from': 'var(--white)',
  '--background-ae-product-all-servic-tile-to': 'var(--white)',
  '--background-ae-order-history-qr-tag': 'var(--background-base-surface-2)',
  '--background-ae-mobility-details-from': 'rgb(var(--white))',
  '--background-ae-mobility-details-to': 'rgb(var(--white))',
  '--content-tag-default': 'var(--_content-flip-default)',
  /* end custom colors  */

  '--_background-base-surface-0': 'var(--blue-100)',
  '--_background-base-surface-1': 'var(--neutral-200)',
  '--_background-base-surface-2': 'var(--neutral-100)',
  '--_background-base-surface-3': 'var(--neutral-300)',
  '--_background-base-overlay': 'rgba(248, 250, 252, 0.8)',
  '--_background_base_surface-2_subdued': 'rgba(240, 240, 246, 0.8)',
  '--_background_base_surface-3_subdued': 'rgba(200, 210, 227, 0.5)',

  '--_content-base-default': 'var(--neutral-900)',
  '--_content-base-subdued': 'var(--neutral-700)',

  '--_content-accent-default': 'var(--blue-600)',
  '--_content-accent-alt': 'var(--teal-500)',

  '--_content-flip-default': 'var(--white)',
  '--_content-flip-subdued': 'var(--neutral-500)',

  '--_stroke-base-default': 'var(--neutral-500)',
  '--_stroke-base-heavy': 'var(--neutral-700)',
  '--_stroke-base-subdued': 'var(--neutral-400)',

  '--_stroke-accent-default': 'var(--blue-500)',
  '--_stroke-accent-alt': 'var(--blue-400)',

  '--_button-background-primary-default': 'var(--blue-500)',
  '--_button-background-primary-hover': 'var(--blue-400)',
  '--_button-background-primary-focus': 'var(--blue-500)',
  '--_button-background-primary-pressed': 'var(--blue-600)',
  '--_button-background-primary-disabled': 'var(--neutral-500)',

  '--_button-background-secondary-default': 'var(--_background-base-surface-2)',
  '--_button-background-secondary-hover': 'var(--blue-500)',
  '--_button-background-secondary-focus': 'var(--_background-base-surface-2)',
  '--_button-background-secondary-pressed': 'var(--blue-600)',
  '--_button-background-secondary-disabled':
    'var(--_background-base-surface-2)',

  '--_button-background-tertiary-default': 'var(--purple-400)',
  '--_button-background-tertiary-hover': 'var(--purple-300)',
  '--_button-background-tertiary-focus': 'var(--purple-400)',
  '--_button-background-tertiary-pressed': 'var(--purple-500)',
  '--_button-background-tertiary-disabled': 'var(--neutral-500)',

  '--_button-background-destructive-default': 'var(--red-600)',
  '--_button-background-destructive-hover': 'var(--red-500)',
  '--_button-background-destructive-focus': 'var(--red-600)',
  '--_button-background-destructive-pressed': 'var(--red-700)',
  '--_button-background-destructive-disabled': 'var(--neutral-500)',

  '--_button-stroke-primary-focus': 'var(--_stroke-accent-default)',
  '--_button-stroke-primary-pressed': 'var(--blue-600)',

  '--_button-stroke-secondary-default': 'var(--_stroke-base-heavy)',
  '--_button-stroke-secondary-hover': 'var(--_stroke-accent-default)',
  '--_button-stroke-secondary-focus': 'var(--_stroke-accent-default)',
  '--_button-stroke-secondary-pressed': 'rgba(255, 255, 255, 0)',
  '--_button-stroke-secondary-disabled': 'var(--_stroke-base-default)',

  '--_button-stroke-tertiary-focus': 'var(--purple-400)',

  '--_button-stroke-destructive-focus': 'var(--red-600)',

  '--_button-stroke-ghost-focus': 'var(--_stroke-accent-default)',
  '--_button-stroke-ghost-hover': 'var(--_stroke-accent-default)',

  '--_button-content-primary-default': 'var(--_content-flip-default)',
  '--_button-content-primary-hover': 'var(--_content-flip-default)',
  '--_button-content-primary-focus': 'var(--_content-flip-default)',
  '--_button-content-primary-pressed': 'var(--_content-flip-default)',
  '--_button-content-primary-disabled': 'var(--_content-base-subdued)',

  '--_button-content-secondary-default': 'var(--_content-base-default)',
  '--_button-content-secondary-hover': 'var(--_content-flip-default)',
  '--_button-content-secondary-focus': 'var(--_content-base-default)',
  '--_button-content-secondary-pressed': 'var(--_content-flip-default)',
  '--_button-content-secondary-disabled': 'var(--_content-base-subdued)',

  '--_button-content-tertiary-default': 'var(--_content-flip-default)',
  '--_button-content-tertiary-hover': 'var(--_content-flip-default)',
  '--_button-content-tertiary-focus': 'var(--_content-flip-default)',
  '--_button-content-tertiary-pressed': 'var(--_content-flip-default)',
  '--_button-content-tertiary-disabled': 'var(--_content-base-subdued)',

  '--_button-content-destructive-default': 'var(--_content-flip-default)',
  '--_button-content-destructive-hover': 'var(--_content-flip-default)',
  '--_button-content-destructive-focus': 'var(--_content-flip-default)',
  '--_button-content-destructive-pressed': 'var(--_content-base-default)',
  '--_button-content-destructive-disabled': 'var(--_content-base-subdued)',

  '--_button-content-ghost-default': 'var(--_content-base-default)',
  '--_button-content-ghost-hover': 'var(--_content-accent-default)',
  '--_button-content-ghost-focus': 'var(--_content-base-default)',
  '--_button-content-ghost-pressed': 'var(--blue-600)',
  '--_button-content-ghost-disabled': 'var(--_content-base-subdued)',

  '--_input-background-unfilled': 'var(--_background-base-surface-2)',
  '--_input-background-filled': 'var(--_background-base-surface-2)',
  '--_input-background-selected': 'var(--blue-500)',
  '--_input-background-hover': 'var(--_background-base-surface-2)',
  '--_input-background-focus': 'var(--_background-base-surface-2)',
  '--_input-background-error': 'var(--_background-base-surface-2)',
  '--_input-background-disabled': 'var(--_background-base-surface-3)',
  '--_input-background-readonly': 'var(--_background-base-surface-3)',

  '--_input-stroke-unfilled': 'var(--_stroke-base-heavy)',
  '--_input-stroke-filled': 'var(--_stroke-base-heavy)',
  '--_input-stroke-selected': 'var(--_stroke-accent-default)',
  '--_input-stroke-hover': 'var(--_stroke-accent-default)',
  '--_input-stroke-focus': 'var(--_stroke-accent-default)',
  '--_input-stroke-error': 'var(--_status-error-default)',
  '--_input-stroke-readonly': 'var(--_stroke-base-default)',
  '--_input-stroke-disabled': 'var(--_stroke-base-default)',

  '--_input-content-unfilled': 'var(--_content-base-subdued)',
  '--_input-content-filled': 'var(--_content-base-default)',
  '--_input-content-selected': 'var(--_content-flip-default)',
  '--_input-content-hover': 'var(--_content-base-default)',
  '--_input-content-focus': 'var(--_content-base-default)',
  '--_input-content-error': 'var(--_status-error-default)',
  '--_input-content-disabled': 'var(--_content-base-subdued)',
  '--_input-content-readonly': 'var(--_content-base-subdued)',

  '--_ticket-priority-1': 'var(--purple-600)',
  '--_ticket-priority-2': 'var(--purple-400)',
  '--_ticket-priority-3': 'var(--purple-200)',

  '--_ticket-status-pendingtechassignment': 'var(--orange-400)',
  '--_ticket-status-techassigned': 'var(--purple-400)',
  '--_ticket-status-techcheckedin': 'var(--green-500)',
  '--_ticket-status-complete': 'var(--teal-500)',
  '--_ticket-status-cancelled': 'var(--neutral-500)',
  '--_ticket-status-pendingcustomer': 'var(--red-600)',
  '--_ticket-status-onhold': 'var(--yellow-500)',
  '--_ticket-priority-premium': '#2a2c89',

  '--_status-warning-default': 'var(--orange-500)',
  '--_status-warning-subdued': 'var(--orange-300)',
  '--_status-info-default': 'var(--purple-500)',
  '--_status-info-subdued': 'var(--purple-300)',
  '--_status-error-default': 'var(--red-600)',
  '--_status-error-subdued': 'var(--red-300)',
  '--_status-success-default': 'var(--green-500)',
  '--_status-success-subdued': 'var(--green-300)',

  '--_user-status-active': 'var(--green-500)',
  '--_user-status-deactivated': 'var(--neutral-500)',

  '--_user-role-admin': 'var(--purple-600)',
  '--_user-role-basic-user': 'var(--purple-400)',
  '--_user-role-super-user': 'var(--purple-200)',

  '--_navigation-surface-primary': '245 249 255',
  '--_navigation-surface-primary-active': '230 236 243',
  '--_navigation-surface-primary-hover': '235 240 245',
  '--_navigation-surface-primary-subdued': 'rgba(245, 249, 255, 0.7)',

  '--_navigation-accent-blue': 'var(--blue-400)',
  '--_navigation-accent-purple': 'var(--purple2-300)',
  '--_navigation-accent-teal': 'var(--teal-400)',
  '--_navigation-accent-green': 'var(--green-300)',
};

export const darkTheme = {
  '--black': '13 18 25',
  '--white': '248 250 252',

  '--neutral-100': '248 250 252',
  '--neutral-200': '241 245 249',
  '--neutral-300': '226 232 240',
  '--neutral-400': '203 213 225',
  '--neutral-500': '148 163 184',
  '--neutral-600': '100 116 139',
  '--neutral-700': '71 85 105',
  '--neutral-800': '51 65 85',
  '--neutral-900': '30 41 59',

  '--purple-100': '221 221 252',
  '--purple-200': '187 186 250',
  '--purple-300': '151 150 243',
  '--purple-400': '123 123 233',
  '--purple-500': '81 83 221',
  '--purple-600': '59 62 190',
  '--purple-700': '42 44 137',
  '--purple-800': '30 32 97',
  '--purple-900': '20 23 71',

  '--green-100': '242 255 244',
  '--green-200': '205 255 211',
  '--green-300': '167 255 178',
  '--green-400': '130 255 145',
  '--green-500': '89 197 102',
  '--green-600': '71 168 83',
  '--green-700': '55 138 65',
  '--green-800': '40 109 49',
  '--green-900': '27 80 34',

  '--teal-100': '242 253 255',
  '--teal-200': '205 249 255',
  '--teal-300': '167 244 255',
  '--teal-400': '130 240 255',
  '--teal-500': '89 184 197',
  '--teal-600': '71 156 168',
  '--teal-700': '55 128 138',
  '--teal-800': '40 101 109',
  '--teal-900': '27 74 80',

  '--blue-100': '236 242 255',
  '--blue-200': '200 221 246',
  '--blue-300': '150 186 255',
  '--blue-400': '108 157 255',
  '--blue-500': '61 121 239',
  '--blue-600': '26 75 171',
  '--blue-700': '43 43 63',
  '--blue-800': '34 34 49',
  '--blue-900': '25 25 37',

  '--purple2-100': '227 218 237',
  '--purple2-200': '165 153 201',
  '--purple2-300': '126 119 179',
  '--purple2-400': '102 95 161',
  '--purple2-500': '72 65 133',
  '--purple2-600': '49 43 107',
  '--purple2-700': '41 36 105',
  '--purple2-800': '28 24 79',
  '--purple2-900': '13 10 51',

  '--yellow-100': '254 252 233',
  '--yellow-200': '254 248 198',
  '--yellow-300': '252 223 86',
  '--yellow-400': '249 203 52',
  '--yellow-500': '233 178 43',
  '--yellow-600': '201 137 32',
  '--yellow-700': '160 98 24',
  '--yellow-800': '132 77 24',
  '--yellow-900': '112 63 24',

  '--orange-100': '255 247 237',
  '--orange-200': '254 237 214',
  '--orange-300': '251 185 121',
  '--orange-400': '249 146 71',
  '--orange-500': '247 115 42',
  '--orange-600': '232 89 33',
  '--orange-700': '192 66 27',
  '--orange-800': '152 53 26',
  '--orange-900': '123 45 23',

  '--red-100': '255 234 239',
  '--red-200': '255 188 202',
  '--red-300': '255 141 166',
  '--red-400': '255 95 130',
  '--red-500': '255 49 94',
  '--red-600': '225 29 72',
  '--red-700': '157 1 35',
  '--red-800': '123 0 27',
  '--red-900': '89 0 20',

  '--pink-100': '253 242 248',
  '--pink-200': '252 231 243',
  '--pink-300': '247 169 211',
  '--pink-400': '242 117 181',
  '--pink-500': '234 76 153',
  '--pink-600': '217 45 119',
  '--pink-700': '188 30 94',
  '--pink-800': '155 27 78',
  '--pink-900': '130 27 67',

  '--accent-primary': 'var(--blue-400)',
  '--accent-blue': 'var(--blue-400)',
  '--accent-purple': 'var(--purple2-300)',
  '--accent-teal': 'var(--teal-400)',
  '--accent-green': 'var(--green-300)',

  '--shadow-ae-product': 'none',
  /* custom colors */
  '--background-ae-empty-cart': '29 29 42',
  '--background-ae-product': '29 29 42',
  '--background-ae-product-hover': '31 31 45',
  '--background-inventory-empty-box-hover': '38 38 55',
  '--background-inventory-recent-update-hover': '41 41 59',
  '--background-table-subrow': '41 41 59',
  '--background-ae-cart-item': '39 39 57',
  '--background-ae-product-all-servic-tile-from': '33 33 47',
  '--background-ae-product-all-servic-tile-to': '25 25 35',
  '--background-ae-order-history-qr-tag': 'var(--blue-600)',
  '--background-ae-mobility-details-from': 'rgb(13, 18, 25)',
  '--background-ae-mobility-details-to': 'rgb(26, 30, 36)',
  '--content-tag-default': 'var(--_content-flip-default)',
  /* end custom colors  */

  '--_background-base-surface-0': 'var(--black)',
  '--_background-base-surface-1': 'var(--blue-900)',
  '--_background-base-surface-2': 'var(--blue-800)',
  '--_background-base-surface-3': 'var(--blue-700)',
  '--_background-base-overlay': 'rgba(13, 18, 25, 0.8)',
  '--_background_base_surface-2_subdued': 'rgba(34, 34, 49, 0.7)',
  '--_background_base_surface-3_subdued': 'rgba(43, 43, 63, 0.5)',

  '--_content-base-default': 'var(--neutral-100)',
  '--_content-base-subdued': 'var(--neutral-500)',
  '--_content-accent-default': 'var(--teal-400)',
  '--_content-accent-alt': 'var(--blue-400)',
  '--_content-flip-default': 'var(--blue-900)',
  '--_content-flip-subdued': 'var(--neutral-600)',

  '--_stroke-base-default': 'var(--neutral-700)',
  '--_stroke-base-heavy': 'var(--neutral-500)',
  '--_stroke-base-subdued': '71, 85, 105, 0.5',
  '--_stroke-accent-default': 'var(--teal-400)',
  '--_stroke-accent-alt': 'var(--teal-500)',

  '--_button-background-primary-default': 'var(--teal-400)',
  '--_button-background-primary-hover': 'var(--teal-300)',
  '--_button-background-primary-focus': 'var(--teal-400)',
  '--_button-background-primary-pressed': 'var(--teal-600)',
  '--_button-background-primary-disabled': 'var(--neutral-500)',

  '--_button-background-secondary-default': 'rgba(255, 255, 255, 0)',
  '--_button-background-secondary-hover': 'var(--teal-400)',
  '--_button-background-secondary-focus': 'rgba(255, 255, 255, 0)',
  '--_button-background-secondary-pressed': 'var(--teal-600)',
  '--_button-background-secondary-disabled': 'rgba(148, 163, 184, 0)',

  '--_button-background-tertiary-default': 'var(--purple-300)',
  '--_button-background-tertiary-hover': 'var(--purple-200)',
  '--_button-background-tertiary-focus': 'var(--purple-300)',
  '--_button-background-tertiary-pressed': 'var(--purple-400)',
  '--_button-background-tertiary-disabled': 'var(--neutral-500)',

  '--_button-background-destructive-default': 'var(--_status-error-default)',
  '--_button-background-destructive-hover': 'var(--red-400)',
  '--_button-background-destructive-focus': 'var(--_status-error-default)',
  '--_button-background-destructive-pressed': 'var(--_status-error-subdued)',
  '--_button-background-destructive-disabled': 'var(--neutral-500)',

  '--_button-stroke-primary-focus': 'var(--_stroke-accent-default)',
  '--_button-stroke-primary-pressed': 'var(--teal-600)',

  '--_button-stroke-secondary-default': 'var(--neutral-400)',
  '--_button-stroke-secondary-hover': 'var(--teal-300)',
  '--_button-stroke-secondary-focus': 'var(--_stroke-accent-default)',
  '--_button-stroke-secondary-pressed': 'rgba(255, 255, 255, 0)',
  '--_button-stroke-secondary-disabled': 'var(--neutral-500)',

  '--_button-stroke-tertiary-focus': 'var(--purple-400)',
  '--_button-stroke-destructive-focus': 'var(--_status-error-default)',
  '--_button-stroke-ghost-focus': 'var(--_stroke-accent-default)',
  '--_button-stroke-ghost-hover': 'var(--_stroke-accent-default)',

  '--_button-content-primary-default': 'var(--_content-flip-default)',
  '--_button-content-primary-hover': 'var(--_content-flip-default)',
  '--_button-content-primary-focus': 'var(--_content-flip-default)',
  '--_button-content-primary-pressed': 'var(--_content-flip-default)',
  '--_button-content-primary-disabled': 'var(--_content-flip-subdued)',

  '--_button-content-secondary-default': 'var(--_content-base-default)',
  '--_button-content-secondary-hover': 'var(--_content-flip-default)',
  '--_button-content-secondary-focus': 'var(--_content-base-default)',
  '--_button-content-secondary-pressed': 'var(--_content-flip-default)',
  '--_button-content-secondary-disabled': 'var(--neutral-500)',

  '--_button-content-tertiary-default': 'var(--_content-flip-default)',
  '--_button-content-tertiary-hover': 'var(--_content-flip-default)',
  '--_button-content-tertiary-focus': 'var(--_content-flip-default)',
  '--_button-content-tertiary-pressed': 'var(--_content-flip-default)',
  '--_button-content-tertiary-disabled': 'var(--neutral-700)',

  '--_button-content-destructive-default': 'var(--_content-flip-default)',
  '--_button-content-destructive-hover': 'var(--_content-flip-default)',
  '--_button-content-destructive-focus': 'var(--_content-flip-default)',
  '--_button-content-destructive-pressed': 'var(--_content-base-default)',
  '--_button-content-destructive-disabled': 'var(--neutral-700)',

  '--_button-content-ghost-default': 'var(--_content-base-default)',
  '--_button-content-ghost-hover': 'var(--_content-accent-default)',
  '--_button-content-ghost-focus': 'var(--_content-base-default)',
  '--_button-content-ghost-pressed': 'var(--teal-700)',
  '--_button-content-ghost-disabled': 'var(--neutral-500)',

  '--_input-background-unfilled': 'var(--_background-base-surface-1)',
  '--_input-background-filled': 'var(--_background-base-surface-1)',
  '--_input-background-selected': 'var(--teal-400)',
  '--_input-background-hover': 'var(--_background-base-surface-2)',
  '--_input-background-focus': 'var(--_background-base-surface-1)',
  '--_input-background-error': 'var(--_background-base-surface-1)',
  '--_input-background-disabled': 'var(--neutral-900)',
  '--_input-background-readonly': 'var(--neutral-900)',

  '--_input-stroke-unfilled': 'var(--_stroke-base-heavy)',
  '--_input-stroke-filled': 'var(--_stroke-base-heavy)',
  '--_input-stroke-selected': 'var(--_stroke-accent-default)',
  '--_input-stroke-hover': 'var(--_stroke-accent-default)',
  '--_input-stroke-focus': 'var(--_stroke-accent-default)',
  '--_input-stroke-error': 'var(--_status-error-default)',
  '--_input-stroke-readonly': 'var(--_stroke-base-default)',
  '--_input-stroke-disabled': 'var(--_stroke-base-default)',

  '--_input-content-unfilled': 'var(--_content-base-subdued)',
  '--_input-content-filled': 'var(--_content-base-default)',
  '--_input-content-selected': 'var(--_content-flip-default)',
  '--_input-content-hover': 'var(--_content-base-default)',
  '--_input-content-focus': 'var(--_content-base-default)',
  '--_input-content-error': 'var(--_status-error-default)',
  '--_input-content-disabled': 'var(--_content-base-subdued)',
  '--_input-content-readonly': 'var(--_content-base-subdued)',

  '--_ticket-priority-1': 'var(--blue-600)',
  '--_ticket-priority-2': 'var(--blue-500)',
  '--_ticket-priority-3': 'var(--blue-300)',

  '--_ticket-status-pendingtechassignment': 'var(--orange-300)',
  '--_ticket-status-techassigned': 'var(--purple-300)',
  '--_ticket-status-techcheckedin': 'var(--green-400)',
  '--_ticket-status-complete': 'var(--teal-400)',
  '--_ticket-status-cancelled': 'var(--neutral-500)',
  '--_ticket-status-pendingcustomer': 'var(--red-500)',
  '--_ticket-status-onhold': 'var(--yellow-400)',
  '--_ticket-priority-premium': '#2a2c89',

  '--_status-warning-default': 'var(--orange-400)',
  '--_status-warning-subdued': 'var(--orange-700)',
  '--_status-info-default': 'var(--purple-300)',
  '--_status-info-subdued': 'var(--purple-700)',
  '--_status-error-default': 'var(--red-500)',
  '--_status-error-subdued': 'var(--red-700)',
  '--_status-success-default': 'var(--green-400)',
  '--_status-success-subdued': 'var(--green-700)',

  '--_user-status-active': 'var(--green-400)',
  '--_user-status-deactivated': 'var(--neutral-400)',

  '--_user-role-admin': 'var(--blue-600)',
  '--_user-role-basic-user': 'var(--blue-500)',
  '--_user-role-super-user': 'var(--blue-300)',

  '--_navigation-surface-primary': '38 38 54',
  '--_navigation-surface-primary-active': '51 51 70',
  '--_navigation-surface-primary-hover': '43 43 60',
  '--_navigation-surface-primary-subdued': 'rgba(38, 38, 54, 0.7)',

  '--_navigation-accent-blue': 'var(--blue-400)',
  '--_navigation-accent-purple': 'var(--purple2-300)',
  '--_navigation-accent-teal': 'var(--teal-400)',
  '--_navigation-accent-green': 'var(--green-300)',
};

export const POST_MESSAGE_EVENTS = {
  update: 'THEME_DESIGNER_UPDATE',
  reset: 'THEME_DESIGNER_RESET',
  changeRoute: 'THEME_DESIGNER_CHANGE_ROUTE',
};

export const defaultBrand: BrandType = {
  mode: 'dark',
  // landing page
  lpTaglineTopRow: 'The future of telecom',
  lpTaglineBottomRow: 'at your fingertips',
  lpSubtext: 'Customizable network solutions made simple.',
  lpBackgroundImage: 'abstract_1',
  lpAccentImage: 'vortex',
  fontFamily: 'Poppins',
};

const toRGBString = (color: string | chroma.Color) => {
  const [r, g, b] = chroma(color).rgb();
  return `${r} ${g} ${b}`;
};

const parseColor = (value: string | undefined): chroma.Color | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  if (chroma.valid(trimmed)) {
    return chroma(trimmed);
  }
  const cssVarMatch = trimmed.match(/^var\(\s*(--[A-Za-z0-9-_]+)\s*\)$/);

  if (cssVarMatch) {
    try {
      const resolved = getComputedStyle(
        document.documentElement,
      ).getPropertyValue(cssVarMatch[1]);
      if (resolved && resolved.trim() && resolved.trim() !== trimmed) {
        return parseColor(resolved.trim());
      }
    } catch (_error) {
      // ignore lookup issues, fall back to other parsing strategies
    }
  }

  const rgbMatch = trimmed.match(
    /^(-?\d{1,3})\s+(-?\d{1,3})\s+(-?\d{1,3})(?:\s*(-?\d{1,3}))?$/,
  );
  if (rgbMatch) {
    const components = rgbMatch
      .slice(1, 4)
      .map((part) => Math.min(255, Math.max(0, Number.parseInt(part, 10))));
    return chroma.rgb(
      components[0] ?? 0,
      components[1] ?? 0,
      components[2] ?? 0,
    );
  }

  return null;
};

export const hasAccessibleContrast = (
  background: string,
  text: string,
  threshold = 4.5,
): boolean => {
  const bgColor = parseColor(background);
  const textColor = parseColor(text);

  if (!bgColor || !textColor) return false;
  return chroma.contrast(bgColor, textColor) >= threshold;
};

export const readCssVariable = (
  variableName: string,
  element: Element = document.documentElement,
): string => {
  if (!variableName.startsWith('--')) return '';
  return getComputedStyle(element).getPropertyValue(variableName).trim();
};

export const applyVariables = (variables: Record<string, string>) => {
  const root = document.documentElement;
  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

// dark theme
const applyBackgroundPaletteDark = (base: string) => {
  if (!chroma.valid(base)) return;
  const color = chroma(base);

  // Scale: darker → base → lighter
  const scale = chroma
    .scale([color.darken(2), color, color.brighten(1.5)])
    .mode('lab')
    .colors(5);

  const navColor = chroma(base);
  const navScale = [
    navColor, // primary
    navColor.brighten(0.5), // hover
    navColor.brighten(1), // active
  ];

  // Map to your exact variable names
  const variables: Record<string, string> = {
    '--_background-base-surface-0': toRGBString(scale[0]),
    '--_background-base-surface-1': toRGBString(scale[2]), // base
    '--_background-base-surface-2': toRGBString(scale[3]),
    '--_background-base-surface-3': toRGBString(scale[4]),
    '--_background-base-overlay': `rgba(${toRGBString(base)} / 0.8)`,
    '--_background_base_surface-2_subdued': `rgba(${toRGBString(
      scale[3],
    )} / 0.7)`,
    '--_background_base_surface-3_subdued': `rgba(${toRGBString(
      scale[4],
    )} / 0.5)`,

    // --- Navigation ---
    '--_navigation-surface-primary': toRGBString(navScale[0]),
    '--_navigation-surface-primary-hover': toRGBString(navScale[1]),
    '--_navigation-surface-primary-active': toRGBString(navScale[2]),
    '--_navigation-surface-primary-subdued': `rgba(${toRGBString(
      navScale[0],
    )} / 0.7)`,

    '--background-ae-empty-cart': toRGBString(scale[4]),
    '--background-ae-product': toRGBString(scale[4]),
    '--background-ae-product-hover': toRGBString(scale[3]),
    '--background-inventory-empty-box-hover': toRGBString(scale[4]),
    '--background-inventory-recent-update-hover': toRGBString(scale[4]),
    '--background-table-subrow': toRGBString(scale[4]),
    '--background-ae-cart-item': toRGBString(scale[4]),
    '--background-ae-product-all-servic-tile-from': toRGBString(scale[3]),
    '--background-ae-product-all-servic-tile-to': toRGBString(scale[3]),
    '--background-ae-order-history-qr-tag': toRGBString(scale[3]),
    '--background-ae-mobility-details-from': toRGBString(scale[2]),
    '--background-ae-mobility-details-to': toRGBString(scale[2]),
    /* end custom colors  */
  };

  applyVariables(variables);
};
const applyPrimaryPaletteDark = (base: string) => {
  if (!chroma.valid(base)) return;
  const btnBase = chroma(base);

  const btnDefault = btnBase; // base
  const btnHover = btnBase.brighten(0.8).saturate(0.3);
  const btnFocus = btnBase; // same as default
  const btnPressed = btnBase.darken(1.2).desaturate(0.3);
  const btnDisabled = btnBase.desaturate(2.5).brighten(0.5);

  const variables: Record<string, string> = {
    '--_button-background-primary-default': toRGBString(btnDefault),
    '--_button-background-primary-hover': toRGBString(btnHover),
    '--_button-background-primary-focus': toRGBString(btnFocus),
    '--_button-background-primary-pressed': toRGBString(btnPressed),
    '--_button-background-primary-disabled': toRGBString(btnDisabled),

    '--_button-stroke-primary-focus': toRGBString(btnFocus),
    '--_button-stroke-primary-pressed': toRGBString(btnPressed),

    '--_button-background-secondary-hover': toRGBString(btnHover),
    '--_button-background-secondary-focus': toRGBString(btnFocus),
    '--_button-background-secondary-pressed': toRGBString(btnPressed),
    '--_button-background-secondary-disabled': toRGBString(btnDisabled),

    '--_button-stroke-secondary-default': toRGBString(btnDefault),
    '--_button-stroke-secondary-hover': toRGBString(btnHover),
    '--_button-stroke-secondary-focus': toRGBString(btnPressed),
    '--_button-stroke-secondary-pressed': toRGBString(btnPressed),
    '--_button-stroke-secondary-disabled': toRGBString(btnDisabled),

    '--_button-content-secondary-default': toRGBString(btnDefault),
    '--_button-content-secondary-focus': 'var(--blue-900)',

    '--_button-stroke-ghost-focus': toRGBString(btnDefault),
    '--_button-stroke-ghost-hover': toRGBString(btnHover),

    '--_button-content-ghost-default': toRGBString(btnDefault),
    '--_button-content-ghost-hover': toRGBString(btnHover),
    '--_button-content-ghost-focus': toRGBString(btnPressed),
    '--_button-content-ghost-pressed': toRGBString(btnPressed),
  };

  applyVariables(variables);
};
const applyStrokePaletteDark = (base: string) => {
  if (!chroma.valid(base)) return;
  const strokeBase = chroma(base);

  const defaultStroke = strokeBase; // base
  const heavyStroke = strokeBase.brighten(1.2); // lighter/brighter
  const subduedStroke = strokeBase.alpha(0.5); // semi-transparent version

  const variables: Record<string, string> = {
    '--_stroke-base-default': toRGBString(defaultStroke),
    '--_stroke-base-heavy': toRGBString(heavyStroke),
    '--_stroke-base-subdued': `${toRGBString(subduedStroke).replaceAll(
      ' ',
      ', ',
    )}, 0.5`,
  };

  applyVariables(variables);
};
const applyContentPaletteDark = (base: string) => {
  if (!chroma.valid(base)) return;
  const contentBase = chroma(base);

  const contentDefault = contentBase; // base color (main text)
  const contentSubdued = contentBase.darken(1.5).desaturate(0.3); // secondary text

  const variables: Record<string, string> = {
    '--_content-base-default': toRGBString(contentDefault),
    '--_content-base-subdued': toRGBString(contentSubdued),
  };

  applyVariables(variables);
};
const applyAccentPaletteDark = (base: string) => {
  if (!chroma.valid(base)) return;
  const accent = chroma(base);
  const tertiaryBtnBase = chroma(base);
  // --- Content accents ---
  const contentDefault = accent; // base
  const contentAlt = accent.darken(0.8).desaturate(0.2); // darker, slightly muted

  // --- Stroke accents ---
  const strokeDefault = accent; // base
  const strokeAlt = accent.darken(1).desaturate(0.3); // darker than default

  const tertiaryBtnDefault = tertiaryBtnBase; // base
  const tertiaryBtnHover = tertiaryBtnBase.brighten(0.8).saturate(0.3);
  const tertiaryBtnFocus = tertiaryBtnBase; // same as default
  const tertiaryBtnPressed = tertiaryBtnBase.darken(1.2).desaturate(0.3);
  const tertiaryBtnDisabled = tertiaryBtnBase.desaturate(2.5).brighten(0.5);

  const flipDefault = readCssVariable('--_content-flip-default');
  const tagHasContrast = hasAccessibleContrast(
    toRGBString(contentDefault),
    flipDefault,
  );

  const variables: Record<string, string> = {
    '--content-tag-default': tagHasContrast
      ? 'var(--_content-flip-default)'
      : 'var(--white)',
    '--_content-accent-default': toRGBString(contentDefault),
    '--_content-accent-alt': toRGBString(contentAlt),
    '--_stroke-accent-default': toRGBString(strokeDefault),
    '--_stroke-accent-alt': toRGBString(strokeAlt),

    '--accent-primary': toRGBString(contentDefault),
    '--accent-blue': toRGBString(contentDefault),
    '--accent-purple': toRGBString(contentDefault),
    '--accent-teal': toRGBString(contentDefault),
    '--accent-green': toRGBString(contentDefault),
    '--_navigation-accent-blue': toRGBString(contentDefault),
    '--_navigation-accent-purple': toRGBString(contentDefault),
    '--_navigation-accent-teal': toRGBString(contentDefault),
    '--_navigation-accent-green': toRGBString(contentDefault),

    '--_button-background-tertiary-default': toRGBString(tertiaryBtnDefault),
    '--_button-background-tertiary-hover': toRGBString(tertiaryBtnHover),
    '--_button-background-tertiary-focus': toRGBString(tertiaryBtnFocus),
    '--_button-background-tertiary-pressed': toRGBString(tertiaryBtnPressed),
    '--_button-background-tertiary-disabled': toRGBString(tertiaryBtnDisabled),

    '--_button-stroke-tertiary-focus': toRGBString(tertiaryBtnFocus),
  };

  applyVariables(variables);
};

// light theme
const applyBackgroundPaletteLight = (base: string) => {
  if (!chroma.valid(base)) return;
  const bgBase = chroma(base);
  const navBase = chroma(base);

  // --- Background scale: lighter above, darker below ---
  const baseHex = bgBase.hex().toLowerCase();
  const isWhiteBase = baseHex === '#ffffff';
  const bgScaleStops = isWhiteBase
    ? [
        bgBase.darken(0.5), // 0 - slightly darker for white base
        bgBase.darken(0.25), // 1 - darker for white base
        bgBase, // 2 - base
        bgBase.darken(1), // 3 - darker
        bgBase.darken(2), // 4 - darkest
      ]
    : [
        bgBase.brighten(2), // 0 - lighter than base
        bgBase.brighten(0.5), // 1 - lighter
        bgBase, // 2 - base
        bgBase.darken(1), // 3 - darker
        bgBase.darken(2), // 4 - darkest
      ];

  const bgScale = chroma.scale(bgScaleStops).mode('lab').colors(5);

  const navScale = chroma
    .scale([navBase.darken(0.5), navBase.darken(0.25), navBase])
    .mode('lab')
    .colors(3);

  const variables: Record<string, string> = {
    // --- Background / surface ---
    '--_background-base-surface-0': toRGBString(bgScale[0]),
    '--_background-base-surface-1': toRGBString(bgScale[2]), // base
    '--_background-base-surface-2': toRGBString(bgScale[1]),
    '--_background-base-surface-3': toRGBString(bgScale[3]),
    '--_background-base-overlay': `rgba(${toRGBString(bgBase)} / 0.8)`,
    '--_background_base_surface-2_subdued': `rgba(${toRGBString(
      bgScale[1],
    )} / 0.7)`,
    '--_background_base_surface-3_subdued': `rgba(${toRGBString(
      bgScale[3],
    )} / 0.5)`,

    // --- Navigation ---
    '--_navigation-surface-primary': toRGBString(navScale[2]), // base
    '--_navigation-surface-primary-hover': toRGBString(navScale[1]),
    '--_navigation-surface-primary-active': toRGBString(navScale[0]),
    '--_navigation-surface-primary-subdued': `rgba(${toRGBString(
      navScale[2],
    )} / 0.7)`,

    '--background-ae-empty-cart': toRGBString(bgScale[1]),
    '--background-ae-product': toRGBString(bgScale[1]),
    '--background-ae-product-hover': toRGBString(bgScale[2]),
    '--background-inventory-empty-box-hover': toRGBString(bgScale[1]),
    '--background-inventory-recent-update-hover': toRGBString(bgScale[2]),
    '--background-table-subrow': toRGBString(bgScale[2]),
    '--background-ae-cart-item': toRGBString(bgScale[2]),
    '--background-ae-product-all-servic-tile-from': toRGBString(bgScale[2]),
    '--background-ae-product-all-servic-tile-to': toRGBString(bgScale[2]),
    '--background-ae-order-history-qr-tag': toRGBString(bgScale[1]),
    '--background-ae-mobility-details-from': toRGBString(bgScale[2]),
    '--background-ae-mobility-details-to': toRGBString(bgScale[2]),
  };
  applyVariables(variables);
};
const applyPrimaryPaletteLight = (base: string) => {
  if (!chroma.valid(base)) return;
  const btnBase = chroma(base);

  const btnDefault = btnBase; // base
  const btnHover = btnBase.brighten(0.8).saturate(0.5);
  const btnFocus = btnBase; // same as default
  const btnPressed = btnBase.darken(0.8).saturate(-0.3);
  const btnDisabled = btnBase.desaturate(2).brighten(0.5);

  const variables: Record<string, string> = {
    '--_button-background-primary-default': toRGBString(btnDefault),
    '--_button-background-primary-hover': toRGBString(btnHover),
    '--_button-background-primary-focus': toRGBString(btnFocus),
    '--_button-background-primary-pressed': toRGBString(btnPressed),
    '--_button-background-primary-disabled': toRGBString(btnDisabled),

    '--_button-stroke-primary-focus': toRGBString(btnFocus),
    '--_button-stroke-primary-pressed': toRGBString(btnPressed),

    '--_button-background-secondary-hover': toRGBString(btnHover),
    '--_button-background-secondary-focus': toRGBString(btnFocus),
    '--_button-background-secondary-pressed': toRGBString(btnPressed),
    '--_button-background-secondary-disabled': toRGBString(btnDisabled),

    '--_button-stroke-secondary-default': toRGBString(btnDefault),
    '--_button-stroke-secondary-hover': toRGBString(btnHover),
    '--_button-stroke-secondary-focus': toRGBString(btnPressed),
    '--_button-stroke-secondary-pressed': toRGBString(btnPressed),
    '--_button-stroke-secondary-disabled': toRGBString(btnDisabled),

    '--_button-content-secondary-default': toRGBString(btnDefault),
    '--_button-content-secondary-focus': 'var(--blue-900)',

    '--_button-stroke-ghost-focus': toRGBString(btnDefault),
    '--_button-stroke-ghost-hover': toRGBString(btnHover),

    '--_button-content-ghost-default': toRGBString(btnDefault),
    '--_button-content-ghost-hover': toRGBString(btnHover),
    '--_button-content-ghost-focus': toRGBString(btnPressed),
    '--_button-content-ghost-pressed': toRGBString(btnPressed),
  };

  applyVariables(variables);
};
const applyStrokePaletteLight = (base: string) => {
  if (!chroma.valid(base)) return;
  const baseStroke = chroma(base);

  const baseDefault = baseStroke; // given base
  const baseHeavy = baseStroke.darken(0.8).saturate(0.3);
  const baseSubdued = baseStroke.brighten(0.8).desaturate(0.5);

  const variables: Record<string, string> = {
    '--_stroke-base-default': toRGBString(baseDefault),
    '--_stroke-base-heavy': toRGBString(baseHeavy),
    '--_stroke-base-subdued': toRGBString(baseSubdued),
  };

  applyVariables(variables);
};
const applyContentPaletteLight = (base: string) => {
  if (!chroma.valid(base)) return;
  const contentBase = chroma(base);

  const contentDefault = contentBase; // main text
  const contentSubdued = contentBase.brighten(0.8).desaturate(0.5); // softer secondary

  const variables: Record<string, string> = {
    '--_content-base-default': toRGBString(contentDefault),
    '--_content-base-subdued': toRGBString(contentSubdued),
  };

  applyVariables(variables);
};
const applyAccentPaletteLight = (base: string) => {
  if (!chroma.valid(base)) return;
  const accent = chroma(base);
  const tertiaryBtnBase = chroma(base);

  // --- Content accents ---
  const contentDefault = accent; // base passed in
  const contentAlt = accent
    .set('hsl.h', accent.get('hsl.h') + 30) // hue shift → teal-ish
    .brighten(1);

  // --- Stroke accents ---
  const strokeDefault = accent.brighten(0.8);
  const strokeAlt = accent.brighten(1.5).saturate(0.3);

  const tertiaryBtnDefault = tertiaryBtnBase; // base
  const tertiaryBtnHover = tertiaryBtnBase.brighten(0.8).saturate(0.5);
  const tertiaryBtnFocus = tertiaryBtnBase; // same as default
  const tertiaryBtnPressed = tertiaryBtnBase.darken(0.8).saturate(-0.3);
  const tertiaryBtnDisabled = tertiaryBtnBase.desaturate(2).brighten(0.5);

  const flipDefault = readCssVariable('--_content-flip-default');
  const tagHasContrast = hasAccessibleContrast(
    toRGBString(contentDefault),
    flipDefault,
  );

  const variables: Record<string, string> = {
    '--content-tag-default': tagHasContrast
      ? 'var(--_content-flip-default)'
      : 'var(--black)',
    '--_content-accent-default': toRGBString(contentDefault),
    '--_content-accent-alt': toRGBString(contentAlt),

    '--_stroke-accent-default': toRGBString(strokeDefault),
    '--_stroke-accent-alt': toRGBString(strokeAlt),

    '--accent-primary': toRGBString(contentDefault),
    '--accent-blue': toRGBString(contentDefault),
    '--accent-purple': toRGBString(contentDefault),
    '--accent-teal': toRGBString(contentDefault),
    '--accent-green': toRGBString(contentDefault),
    '--_navigation-accent-blue': toRGBString(contentDefault),
    '--_navigation-accent-purple': toRGBString(contentDefault),
    '--_navigation-accent-teal': toRGBString(contentDefault),
    '--_navigation-accent-green': toRGBString(contentDefault),

    '--_button-background-tertiary-default': toRGBString(tertiaryBtnDefault),
    '--_button-background-tertiary-hover': toRGBString(tertiaryBtnHover),
    '--_button-background-tertiary-focus': toRGBString(tertiaryBtnFocus),
    '--_button-background-tertiary-pressed': toRGBString(tertiaryBtnPressed),
    '--_button-background-tertiary-disabled': toRGBString(tertiaryBtnDisabled),

    '--_button-stroke-tertiary-focus': toRGBString(tertiaryBtnFocus),
  };

  applyVariables(variables);
};

export const applyColors = (
  brand: Pick<
    BrandType,
    | 'accentColor'
    | 'backgroundColor'
    | 'fontColor'
    | 'primaryColor'
    | 'strokeColor'
    | 'mode'
  >,
) => {
  if (brand.mode === 'light') {
    applyVariables(lightTheme);
    if (brand.backgroundColor)
      applyBackgroundPaletteLight(brand.backgroundColor);
    if (brand.primaryColor) applyPrimaryPaletteLight(brand.primaryColor);
    if (brand.accentColor) applyAccentPaletteLight(brand.accentColor);
    if (brand.strokeColor) applyStrokePaletteLight(brand.strokeColor);
    if (brand.fontColor) applyContentPaletteLight(brand.fontColor);
  } else if (brand.mode === 'dark') {
    applyVariables(darkTheme);
    if (brand.backgroundColor)
      applyBackgroundPaletteDark(brand.backgroundColor);
    if (brand.primaryColor) applyPrimaryPaletteDark(brand.primaryColor);
    if (brand.accentColor) applyAccentPaletteDark(brand.accentColor);
    if (brand.strokeColor) applyStrokePaletteDark(brand.strokeColor);
    if (brand.fontColor) applyContentPaletteDark(brand.fontColor);
  }
};

export const googleFonts = [
  'Inter',
  'Roboto',
  'Montserrat',
  'Open Sans',
  'Lato',
  'Poppins',
  'Rubik',
  'DM Sans',
  'Source Sans Pro',
  'Ubuntu',
];

export const systemFonts = [
  'Segoe UI',
  'Helvetica',
  'Arial',
  'Times New Roman',
  'Georgia',
  'Garamond',
];

export const applyFontPreload = (font: string) => {
  if (!googleFonts.includes(font)) return;

  const fontUrl = `https://fonts.googleapis.com/css2?family=${font.replace(
    / /g,
    '+',
  )}:wght@300;400;500;600&display=swap`;

  const preloadId = 'dynamic-font-preload';
  if (!document.getElementById(preloadId)) {
    const preload = document.createElement('link');
    preload.id = preloadId;
    preload.rel = 'preload';
    preload.as = 'style';
    preload.href = fontUrl;
    document.head.appendChild(preload);
  }
};

export const applyFont = (font: string) => {
  const root = document.documentElement;
  root.style.setProperty('--font-family-sans', font);

  if (googleFonts.includes(font)) {
    const linkId = 'dynamic-font';
    let link = document.getElementById(linkId) as HTMLLinkElement | null;

    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    link.href = `https://fonts.googleapis.com/css2?family=${font.replace(
      / /g,
      '+',
    )}:wght@300;400;500;600&display=swap`;
  }
};

export const applyFavicon = (brand: BrandType | null) => {
  if (brand) {
    try {
      const links = document.querySelectorAll(
        "link[rel~='icon'], link[rel='shortcut icon']",
      );
      links.forEach((link) => link.parentNode?.removeChild(link));
      const tmp = document.createElement('link');
      tmp.rel = 'icon';
      tmp.href = 'data:,' + Date.now(); // tiny unique data URI
      document.head.appendChild(tmp);
    } catch (_) {
      // no-op
    }
  }
};

export const subDomain = ((): string => {
  const baseDomain = `.granitemsp.com`;
  const host = window.location.hostname;

  // Handle localhost (with or without port)
  if (host.startsWith('localhost')) {
    return 'localhost';
  }

  if (!host.endsWith(baseDomain)) return '';

  return host.replace(baseDomain, '');
})();

// WL = whitelabel
export const hasWLAccess = (() => {
  const reserved = [
    // 'localhost',
    'granitemsp.com',
    'www.granitemsp.com',
    // treat DEV as white-label
    // 'dev.granitemsp.com',
    'staging.granitemsp.com',
    'www.staging.granitemsp.com',
    'uat.granitemsp.com',
    'www.uat.granitemsp.com',
  ];

  const host = window.location.hostname;
  return !reserved.includes(host);
})();

export const setWlLocation = () => {
  if (!hasWLAccess) return;
  const params = new URLSearchParams(window.location.search);
  const rawState = params.get('state');
  try {
    if (rawState && QuoteLocationSchema.parse(JSON.parse(rawState)))
      localStorage.setItem('wl-location', rawState);
  } catch (error) {
    localStorage.removeItem('wl-location');
  }
};

export const removeWlLocation = () => localStorage.removeItem('wl-location');

const ROUTES = {
  HOME: '/',
  ORDER_SERVICES: '/access-express/v3/order-services/access',
  PRODUCT_ALL: '/access-express/v3/access/product/All',
} as const;

export const setLocationFromWL = async (data: {
  companyName: string;
  requester: string;
}): Promise<string> => {
  const location = localStorage.getItem('wl-location');
  removeWlLocation();

  try {
    if (location) {
      const parsed = QuoteLocationSchema.safeParse(JSON.parse(location));
      if (!parsed.success) return ROUTES.ORDER_SERVICES;
      const parsedAddress = parsed.data;

      const existingDraft = await getLastDraftOrder();
      const dataForPostOrUpdate = {
        additional_details: {
          configs: {
            download: { label: '500 Mbps', value: '500' },
            save_default: false,
            term: { label: '3 years', value: '3' },
          },
        },
        locations: [
          {
            address: parsedAddress,
            order_items: [],
          },
        ],
      };
      if (existingDraft?.id) {
        try {
          const draft = await updateOrder(
            dataForPostOrUpdate as PostDraftRequest,
            existingDraft.id,
          );
          return draft?.id ? ROUTES.PRODUCT_ALL : ROUTES.ORDER_SERVICES;
        } catch (err) {
          return ROUTES.ORDER_SERVICES;
        }
      } else {
        try {
          const draft = await postOrder({
            ...dataForPostOrUpdate,
            customer_name: data.companyName,
            requester: data.requester,
            status: 'draft',
          } as PostDraftRequest);
          return draft?.id ? ROUTES.PRODUCT_ALL : ROUTES.ORDER_SERVICES;
        } catch (err) {
          return ROUTES.ORDER_SERVICES;
        }
      }
    }
    return ROUTES.HOME;
  } catch {
    removeWlLocation();
    return ROUTES.HOME;
  }
};

export const generateBrandGradient = (
  primaryColor?: string,
  accentColor?: string,
) => {
  const defaultGradient =
    'linear-gradient(89.98deg, #7E77B3 0.95%, #8989FF 24.96%, #6C9DFF 49.86%, #78CFDA 74.97%, #9FF4A9 99.98%)';

  if (!primaryColor && !accentColor) {
    return defaultGradient;
  }

  try {
    const baseColor = primaryColor || accentColor || '#7E77B3';
    const highlightColor = accentColor || primaryColor || '#9FF4A9';

    const intermediate1 = chroma.mix(baseColor, highlightColor, 0.25, 'lab');
    const intermediate2 = chroma.mix(baseColor, highlightColor, 0.75, 'lab');

    const gradientColors = [
      baseColor,
      intermediate1,
      chroma.mix(baseColor, highlightColor, 0.5, 'lab'),
      intermediate2,
      highlightColor,
    ];

    const gradientStops = gradientColors
      .map((color, index) => {
        const percentage = (index / (gradientColors.length - 1)) * 100;
        const colorHex =
          typeof color === 'string' ? chroma(color).hex() : color.hex();
        return `${colorHex} ${percentage.toFixed(2)}%`;
      })
      .join(',');

    return `linear-gradient(89.98deg,${gradientStops})`;
  } catch (error) {
    console.warn('Error generating brand gradient:', error);
    return defaultGradient;
  }
};
